import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, CheckCircle2, Handshake, Ticket, TrendingUp, Users, Trophy, Star,
  Instagram, Music2, Lock, Link2, Sparkles, Award,
} from "lucide-react";
import { StatCard, SectionCard, Tabs, fmt } from "./parts";
import { TrendChart } from "./charts";

const TABS = [
  { key: "overview", label: "Overview", Icon: TrendingUp },
  { key: "collaborations", label: "Collaborations", Icon: Handshake },
];

const PLATFORM_ICON = { instagram: Instagram, tiktok: Music2 };
const STATUS = { accepted: "bg-emerald-100 text-emerald-700", completed: "bg-indigo-100 text-indigo-700", pending: "bg-amber-100 text-amber-700", declined: "bg-zinc-100 text-zinc-600" };

function Metric({ label, value }) {
  const has = value !== null && value !== undefined && value !== "";
  return <div><p className="font-display text-lg font-bold text-zinc-900">{has ? (typeof value === "number" ? fmt(value) : value) : "—"}</p><p className="text-[11px] text-zinc-400">{label}</p></div>;
}

function SocialInsights({ social, onConnect }) {
  if (social?.connected && social.platforms?.length) {
    const f = social.trends?.followers || [];
    return (
      <SectionCard testid="creator-social-live" title="Instagram insights" subtitle="Synced from your connected account — real figures only, never estimated">
        {social.platforms.map((p) => (
          <div key={p.platform} data-testid={`creator-social-${p.platform}`} className="mb-4 rounded-2xl border border-zinc-100 bg-white p-4">
            <div className="flex items-center gap-2">
              {p.profile_image ? <img src={p.profile_image} referrerPolicy="no-referrer" alt="" className="h-8 w-8 rounded-full object-cover" /> : <Instagram className="h-4 w-4 text-rose-500" />}
              <span className="text-sm font-semibold text-zinc-700">@{p.username}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Metric label="Followers" value={p.followers} />
              <Metric label="Following" value={p.following} />
              <Metric label="Posts" value={p.media_count} />
              <Metric label="Engagement" value={p.engagement_rate != null ? `${p.engagement_rate}%` : null} />
              <Metric label="Avg likes / post" value={p.avg_likes} />
              <Metric label="Avg comments / post" value={p.avg_comments} />
              <Metric label="Reach" value={p.reach} />
              <Metric label="Views" value={p.views} />
            </div>
            {p.reach == null && p.views == null && (
              <p data-testid="creator-reach-unavailable" className="mt-2 text-xs text-zinc-400">Reach &amp; views aren't available with the current Instagram permissions.</p>
            )}
            {p.top_media?.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-sm font-semibold text-zinc-700">Top recent posts</p>
                <div className="flex gap-3 overflow-x-auto no-scrollbar">
                  {p.top_media.map((m) => (
                    <a key={m.id} href={m.permalink} target="_blank" rel="noreferrer" className="w-24 shrink-0">
                      <div className="h-24 w-24 overflow-hidden rounded-lg bg-zinc-100 ring-1 ring-zinc-200">{m.thumbnail && <img src={m.thumbnail} referrerPolicy="no-referrer" alt="" className="h-full w-full object-cover" />}</div>
                      <p className="mt-1 truncate text-[11px] text-zinc-500">{fmt(m.like_count ?? 0)} likes · {fmt(m.comment_count ?? 0)} comments</p>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        <div className="mt-2">
          <p className="mb-1 text-sm font-semibold text-zinc-700">Follower growth over time</p>
          {social.enough_history ? <TrendChart data={f} color="#059669" /> : (
            <div data-testid="creator-growth-nodata" className="flex h-32 items-center justify-center rounded-xl bg-zinc-50 px-4 text-center text-sm text-zinc-400">We're collecting daily snapshots — your follower growth chart will appear here once there's enough history.</div>
          )}
        </div>
      </SectionCard>
    );
  }
  return (
    <div data-testid="creator-social-locked" className="rounded-3xl border border-dashed border-zinc-300 bg-white p-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-900 text-white"><Lock className="h-5 w-5" /></div>
      <h3 className="mt-3 font-display text-lg font-bold text-zinc-900">Follower &amp; reach insights</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">Connect Instagram or TikTok to unlock your verified follower growth, reach and engagement over time. We never show estimated or sample numbers.</p>
      <button data-testid="creator-social-connect" onClick={onConnect} className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Link2 className="h-4 w-4" /> Connect Instagram</button>
    </div>
  );
}

export function CreatorAnalytics({ data, onConnect }) {
  const [tab, setTab] = useState("overview");
  const navigate = useNavigate();
  const a = data.activity;

  return (
    <div className="space-y-6">
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "overview" && (
        <div className="space-y-6">
          {a.founding && (
            <div data-testid="creator-founding-badge" className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600"><Award className="h-5 w-5" /></div>
              <div><p className="font-display font-bold text-zinc-900">Founding Creator</p><p className="text-sm text-zinc-500">You're one of Nearfluence's earliest creators — thank you!</p></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard testid="cstat-applications" label="Applications sent" metric={{ value: a.applications_sent }} Icon={Send} tint="text-sky-600 bg-sky-100" index={0} />
            <StatCard testid="cstat-acceptance" label="Acceptance rate" metric={{ value: a.applications_sent ? `${a.acceptance_rate}%` : "—" }} Icon={CheckCircle2} tint="text-emerald-600 bg-emerald-100" index={1} />
            <StatCard testid="cstat-collabs" label="Collaborations" metric={{ value: a.collaborations }} Icon={Handshake} tint="text-violet-600 bg-violet-100" index={2} />
            <StatCard testid="cstat-completed" label="Completed" metric={{ value: a.collaborations_completed }} Icon={Trophy} tint="text-indigo-600 bg-indigo-100" index={3} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard testid="cstat-events" label="Events attended" metric={{ value: a.events_attended }} Icon={Ticket} tint="text-fuchsia-600 bg-fuchsia-100" index={0} />
            <StatCard testid="cstat-event-reach" label="Event reach shared" metric={{ value: a.event_reach }} Icon={TrendingUp} tint="text-amber-600 bg-amber-100" index={1} />
            <StatCard testid="cstat-connections" label="Network connections" metric={{ value: a.connections }} Icon={Users} tint="text-emerald-600 bg-emerald-100" index={2} />
            <StatCard testid="cstat-followers" label="Followers (self-reported)" metric={{ value: a.self_reported_followers ? fmt(a.self_reported_followers) : "—" }} Icon={Instagram} tint="text-rose-600 bg-rose-100" index={3} />
          </div>
          {(a.event_reach > 0 || a.self_reported_followers > 0) && (
            <p className="-mt-3 text-xs text-zinc-400">Event reach and follower counts are self-reported until your accounts are connected via API.</p>
          )}

          {a.rating_count > 0 && (
            <div data-testid="creator-rating" className="flex items-center gap-3 rounded-2xl border border-zinc-100 bg-white p-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-500"><Star className="h-5 w-5 fill-amber-400" /></div>
              <div><p className="font-display text-xl font-bold text-zinc-900">{a.rating_avg.toFixed(1)} <span className="text-sm font-normal text-zinc-400">/ 5</span></p><p className="text-sm text-zinc-500">from {a.rating_count} business review{a.rating_count === 1 ? "" : "s"}</p></div>
            </div>
          )}

          <SectionCard testid="creator-activity-trend" title="Activity — last 90 days" subtitle={`${a.applications_sent} application${a.applications_sent === 1 ? "" : "s"} · ${a.collaborations} collaboration${a.collaborations === 1 ? "" : "s"}`}>
            {(data.series?.applications || []).some((p) => p.value > 0) ? (
              <TrendChart data={data.series.applications} color="#0EA5E9" />
            ) : (
              <div className="flex h-40 flex-col items-center justify-center gap-3 text-center text-sm text-zinc-400">
                <p>No applications in the last 90 days yet.</p>
                <button onClick={() => navigate("/opportunities")} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700">Find opportunities</button>
              </div>
            )}
          </SectionCard>

          <SocialInsights social={data.social} onConnect={onConnect} />
        </div>
      )}

      {tab === "collaborations" && (
        <div className="space-y-3">
          {data.collaborations_list?.length ? data.collaborations_list.map((c) => (
            <div key={c.id} data-testid={`creator-collab-${c.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-100 bg-white p-4">
              {c.business_logo ? <img src={c.business_logo} alt="" referrerPolicy="no-referrer" className="h-11 w-11 rounded-xl object-cover" /> : <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 font-display font-bold text-emerald-600">{c.business_name?.[0]}</div>}
              <div className="min-w-0 flex-1">
                <p className="truncate font-display font-semibold text-zinc-900">{c.title}</p>
                <p className="truncate text-sm text-zinc-500">{c.business_name}</p>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS[c.status] || STATUS.pending}`}>{c.status}</span>
            </div>
          )) : (
            <div data-testid="creator-collabs-empty" className="rounded-3xl border border-dashed border-zinc-300 bg-white p-10 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><Sparkles className="h-5 w-5" /></div>
              <p className="mt-3 font-display text-lg font-bold text-zinc-800">No collaborations yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">Apply to local opportunities and once a business accepts you, your collaborations will show up here.</p>
              <button onClick={() => navigate("/opportunities")} className="mt-4 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">Browse opportunities</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
